import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Announcer, playCue } from './lib/audio.js';
import {
  joinGame,
  submitClaim,
  syncMarks,
  ticketRows,
  watchBoard,
  watchGame,
  watchLive,
  watchPlayer,
  type FalseClaimPolicy,
  type Game,
  type LiveState,
  type PlayerState,
  type Reward,
} from './lib/game.js';

/** Parses `/join/{gameId}#{token}` — the token rides in the fragment. */
function readInvite(): { gameId: string; token: string } | null {
  const match = window.location.pathname.match(/\/join\/([^/]+)/);
  const token = window.location.hash.replace(/^#/, '');
  if (!match?.[1] || !token) return null;
  return { gameId: decodeURIComponent(match[1]), token: decodeURIComponent(token) };
}

const REJECTION_COPY: Record<string, string> = {
  PATTERN_INCOMPLETE: "Not yet — that pattern isn't complete on your ticket.",
  LAST_NUMBER_NOT_IN_PATTERN: "The number just called isn't part of that pattern.",
  CLAIMED_TOO_LATE: "Too late — you've marked more numbers than this prize needs.",
  REWARD_FULLY_AWARDED: 'Someone beat you to it. No penalty — your ticket was good.',
  ALREADY_WON: "You've already won this one.",
  BLOCKED_BY_BOGEY: "You're out of this prize after an earlier wrong claim.",
  IN_COOLDOWN: 'Hold on a moment before claiming again.',
  STRIKE_LIMIT_REACHED: "You've used up your claims for this game.",
  STALE_CLAIM: 'That arrived too late for the number it was for. Try the next one.',
  GAME_NOT_RUNNING: "The game isn't running right now.",
};

function penaltyNote(policy: FalseClaimPolicy, cooldownSec: number, maxStrikes: number): string {
  switch (policy) {
    case 'NONE':
      return 'Wrong claims cost you nothing.';
    case 'COOLDOWN':
      return `A wrong claim locks claiming for ${cooldownSec} seconds.`;
    case 'BOGEY':
      return 'A wrong claim knocks you out of that prize for good.';
    case 'STRIKES':
      return `${maxStrikes} wrong claims and you're out of the game.`;
  }
}

export default function App() {
  const invite = useMemo(readInvite, []);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [tookOver, setTookOver] = useState(false);
  const [joining, setJoining] = useState(false);

  const announcer = useRef(new Announcer());

  const join = useCallback(async () => {
    if (!invite) return;
    // Must happen inside the click handler, or the browser blocks all audio.
    announcer.current.unlock();
    setJoining(true);
    setJoinError(null);
    try {
      const result = await joinGame(invite.gameId, invite.token);
      setPlayerId(result.playerId);
      setTookOver(result.tookOverDevice);
    } catch (error) {
      setJoinError(
        error instanceof Error ? error.message : "That invite link didn't work.",
      );
    } finally {
      setJoining(false);
    }
  }, [invite]);

  if (!invite) {
    return (
      <main className="centered">
        <h1>Company Tambola</h1>
        <p className="muted">
          Open the link from your invitation email to get your ticket.
        </p>
      </main>
    );
  }

  if (!playerId) {
    return (
      <main className="centered">
        <h1>You're invited</h1>
        <p className="muted">Tap below to pick up your ticket and join the game.</p>
        {joinError && <p className="error">{joinError}</p>}
        <button className="primary" onClick={join} disabled={joining}>
          {joining ? 'Joining…' : 'Join the game'}
        </button>
      </main>
    );
  }

  return (
    <GameScreen
      gameId={invite.gameId}
      playerId={playerId}
      announcer={announcer.current}
      tookOver={tookOver}
    />
  );
}

function GameScreen({
  gameId,
  playerId,
  announcer,
  tookOver,
}: {
  gameId: string;
  playerId: string;
  announcer: Announcer;
  tookOver: boolean;
}) {
  const [game, setGame] = useState<Game | null>(null);
  const [live, setLive] = useState<LiveState | null>(null);
  const [player, setPlayer] = useState<PlayerState | null>(null);
  const [board, setBoard] = useState<number[] | null>(null);
  const [marks, setMarks] = useState<Set<number>>(new Set());
  const [notice, setNotice] = useState<string | null>(null);
  const [claiming, setClaiming] = useState<string | null>(null);

  const lastWinAt = useRef<number | null>(null);
  const wasTerminal = useRef(false);
  const syncTimer = useRef<number | undefined>(undefined);
  const seededMarks = useRef(false);

  useEffect(() => watchGame(gameId, setGame), [gameId]);
  useEffect(() => watchBoard(gameId, setBoard), [gameId]);
  useEffect(() => watchPlayer(gameId, playerId, setPlayer), [gameId, playerId]);

  useEffect(
    () =>
      watchLive(gameId, (next) => {
        setLive(next);
        announcer.announce(next?.lastNumber);

        // A win chimes for everyone — the shared reaction is most of the fun.
        if (next?.lastWin && next.lastWin.atMs !== lastWinAt.current) {
          lastWinAt.current = next.lastWin.atMs;
          playCue('win');
        }
        const terminal = next?.status === 'COMPLETED' || next?.status === 'CANCELLED';
        if (terminal && !wasTerminal.current) {
          wasTerminal.current = true;
          playCue('gameover');
        }
      }),
    [gameId, announcer],
  );

  // Server marks seed the local set once, so switching device restores them;
  // after that local taps are authoritative and instant.
  useEffect(() => {
    if (!seededMarks.current && player?.marks?.length) {
      seededMarks.current = true;
      setMarks(new Set(player.marks));
    }
  }, [player]);

  const toggleMark = useCallback(
    (value: number) => {
      setMarks((current) => {
        const next = new Set(current);
        if (next.has(value)) next.delete(value);
        else next.add(value);

        window.clearTimeout(syncTimer.current);
        syncTimer.current = window.setTimeout(() => {
          void syncMarks(gameId, playerId, [...next]);
        }, 1500);
        return next;
      });
    },
    [gameId, playerId],
  );

  const ticket = player?.tickets?.[0];

  const claim = useCallback(
    async (reward: Reward) => {
      if (!ticket || !live || claiming) return;
      setClaiming(reward.id);
      try {
        const result = await submitClaim(gameId, reward.id, ticket.id, live.calledCount);
        if (result.valid) {
          setNotice(
            result.rank && result.rank > 1
              ? `You won ${reward.label} — prize #${result.rank}!`
              : `You won ${reward.label}!`,
          );
        } else {
          const base = REJECTION_COPY[result.reason] ?? "That claim didn't stand.";
          setNotice(base);
        }
      } catch {
        setNotice('Could not send that claim. Check your connection.');
      } finally {
        setClaiming(null);
      }
    },
    [gameId, ticket, live, claiming],
  );

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  if (!ticket) {
    return (
      <main className="centered">
        <p className="muted">Loading your ticket…</p>
      </main>
    );
  }

  const isLive = live?.status === 'RUNNING' || live?.status === 'PAUSED';
  const called = new Set(board ?? live?.recent ?? []);
  const rewards = (game?.rewards ?? []).filter((reward) => reward.enabled);
  const settings = game?.settings;

  return (
    <main className="game">
      <header>
        <h1>{game?.companyName?.trim() || 'Tambola Fun'}</h1>
        <span className="muted">{live?.calledCount ?? 0}/90</span>
      </header>

      {tookOver && (
        <p className="banner">
          You're now playing on this device. Your other device has been signed out.
        </p>
      )}

      <section className="current">
        <div className="number">{live?.lastNumber ?? '—'}</div>
        <div className="muted">
          {live?.status === 'COMPLETED'
            ? 'Game over'
            : live?.status === 'PAUSED'
              ? 'Paused'
              : live?.lastNumber == null
                ? 'Waiting for the first number'
                : ''}
        </div>
      </section>

      <section className="recent">
        {(live?.recent ?? []).map((value) => (
          <span key={value} className="chip">
            {value}
          </span>
        ))}
        {!live?.recent?.length && <span className="muted">No numbers yet</span>}
      </section>

      {live?.lastWin && (
        <p className="banner">
          {live.lastWin.rewardLabel} claimed by {live.lastWin.playerName}
        </p>
      )}

      <div className="ticket-card">
        <div className="ticket-title">{game?.title ?? 'Your ticket'}</div>
        <div className="ticket">
        {ticketRows(ticket).map((row, rowIndex) =>
          row.map((cell, colIndex) => {
            const key = `${rowIndex}-${colIndex}`;
            if (cell == null) return <div key={key} className="cell blank" aria-hidden="true" />;
            const marked = marks.has(cell);
            const wasCalled = called.has(cell);
            return (
              <button
                key={key}
                type="button"
                className={`cell${marked ? ' marked' : ''}${wasCalled && !marked ? ' called' : ''}`}
                onClick={() => isLive && toggleMark(cell)}
                aria-pressed={marked}
                aria-label={
                  marked
                    ? `${cell}, marked`
                    : wasCalled
                      ? `${cell}, called but not marked`
                      : String(cell)
                }
              >
                {cell}
              </button>
            );
          }),
        )}
        </div>
      </div>

      <section className="claims">
        <h2>Claim</h2>
        <div className="claim-buttons">
          {rewards.map((reward) => {
            const won = player?.wonRewardIds?.includes(reward.id);
            const blocked = player?.blockedRewardIds?.includes(reward.id);
            const closed = reward.winnersAwarded >= reward.winnersAllowed;
            return (
              <button
                key={reward.id}
                onClick={() => claim(reward)}
                /* Never hint whether a claim would succeed: the whole point of
                   the penalty is that deciding when to claim is the game. Only
                   genuinely closed prizes are disabled. */
                disabled={!isLive || won || blocked || closed || claiming !== null}
                className={won ? 'won' : blocked ? 'blocked' : ''}
              >
                {reward.label}
                {won ? ' ✓' : blocked ? ' ✕' : ''}
              </button>
            );
          })}
        </div>
        {settings && (
          <p className="muted small">
            {penaltyNote(
              settings.falseClaimPolicy,
              settings.falseClaimParams?.cooldownSec ?? 15,
              settings.falseClaimParams?.maxStrikes ?? 3,
            )}
          </p>
        )}
      </section>

      {notice && <div className="toast">{notice}</div>}
    </main>
  );
}
